import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MapPin,
  Clock,
  Shirt,
  ChevronRight,
  ImageIcon,
  Edit3,
  Trash2,
  Calendar,
  ChevronDown,
  X,
  Camera,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Modal, Thumb, inputClass, LoadingState, ErrorState } from '@/components/ui';
import { PickerSheet, PickerRow } from '@/features/wardrobe/components/PickerSheet';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { downscaleToBase64 } from '@/lib/image';
import { OCCASIONS } from '@/lib/constants';
import { formatDate, cn } from '@/lib/utils';
import type { Wear, FitPiece, Photo } from '@/lib/types';

interface WearResponse {
  wear: Wear;
  pieces: FitPiece[];
  photos: Photo[];
}

export function FitWearPage() {
  const { id, wearId } = useParams<{ id: string; wearId: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi<WearResponse>(wearId ? `/wears/${wearId}` : null);

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showOccasion, setShowOccasion] = useState(false);
  const [photoView, setPhotoView] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  // Edit form state.
  const [wornDate, setWornDate] = useState('');
  const [occasion, setOccasion] = useState('');
  const [locationText, setLocationText] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (data?.wear) {
      setWornDate(data.wear.wornDate.slice(0, 10));
      setOccasion(data.wear.occasion ?? '');
      setLocationText(data.wear.locationText ?? '');
      setNotes(data.wear.notes ?? '');
    }
  }, [data]);

  const handleSaveEdit = async () => {
    if (!wearId) return;
    setWorking(true);
    try {
      await api.patch(`/wears/${wearId}`, {
        wornDate,
        occasion: occasion || undefined,
        locationText: locationText || undefined,
        notes: notes || undefined,
      });
      setShowEdit(false);
      refetch();
    } finally {
      setWorking(false);
    }
  };

  // Add / remove photos for this wear — persisted immediately.
  const addPhotos = async (files: FileList | null) => {
    if (!files || !wearId) return;
    setPhotoBusy(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const image = await downscaleToBase64(file);
          await api.post('/photos', { ownerType: 'fit_wear', ownerId: wearId, image });
        } catch {
          /* skip a failed file */
        }
      }
      refetch();
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async (photoId: string) => {
    setPhotoBusy(true);
    try {
      await api.delete(`/photos/${photoId}`);
      refetch();
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!wearId) return;
    setWorking(true);
    try {
      await api.delete(`/wears/${wearId}`);
      navigate(`/fits/${id}`);
    } finally {
      setWorking(false);
      setShowDelete(false);
    }
  };

  return (
    <Page>
      <PageHeader section="Wear Details" backHref={`/fits/${id}`} backLabel="Back to Fit" />
      <Main className="space-y-4">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {data && (
          <>
            <Card className="p-4 flex items-start justify-between">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{formatDate(data.wear.wornDate)}</h1>
                {data.wear.fitId && (
                  <Link to={`/fits/${data.wear.fitId}`} className="text-sm text-primary hover:underline flex items-center gap-1 mt-1">
                    {data.wear.fitName ?? 'View Fit'}
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
              {data.wear.fitVersionNumber != null && (
                <span className="px-2 py-1 bg-secondary rounded-lg text-xs font-medium text-muted-foreground">
                  v{data.wear.fitVersionNumber}
                </span>
              )}
            </Card>

            <Card className="overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  Photos ({data.photos.length})
                </h2>
                <label
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer',
                    photoBusy && 'opacity-50 pointer-events-none',
                  )}
                >
                  <Camera className="w-3.5 h-3.5" />
                  {photoBusy ? 'Uploading…' : 'Add Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => addPhotos(e.target.files)}
                  />
                </label>
              </div>
              <div className="p-4">
                {data.photos.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    No photos yet. Add one to remember this look.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {data.photos.map((photo) => (
                      <div key={photo.id} className="relative aspect-[3/4]">
                        <button
                          onClick={() => setPhotoView(photo.url)}
                          className="w-full h-full rounded-xl overflow-hidden bg-secondary"
                        >
                          <img src={photo.url} alt="Wear" className="w-full h-full object-cover" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removePhoto(photo.id)}
                          disabled={photoBusy}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
                          aria-label="Delete photo"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground">Details</h2>
              </div>
              <div className="divide-y divide-border">
                {data.wear.locationText && (
                  <Detail icon={<MapPin className="w-4 h-4 text-muted-foreground" />} label="Location" value={data.wear.locationText} />
                )}
                {data.wear.occasion && (
                  <Detail
                    icon={<Clock className="w-4 h-4 text-muted-foreground" />}
                    label="Occasion"
                    value={OCCASIONS.find((o) => o.id === data.wear.occasion)?.label ?? data.wear.occasion}
                  />
                )}
                {data.wear.notes && (
                  <div className="p-4">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm text-foreground">{data.wear.notes}</p>
                  </div>
                )}
                {!data.wear.locationText && !data.wear.occasion && !data.wear.notes && (
                  <p className="text-sm text-muted-foreground p-4">No additional details.</p>
                )}
              </div>
            </Card>

            {data.pieces.length > 0 && (
              <Card className="overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Shirt className="w-4 h-4 text-muted-foreground" />
                    Items Worn ({data.pieces.length})
                  </h2>
                </div>
                <div className="divide-y divide-border">
                  {data.pieces.map((piece) => (
                    <Link
                      key={piece.id}
                      to={`/wardrobe/item/${piece.itemId}`}
                      className="p-4 flex items-center gap-3 hover:bg-secondary/50 transition-colors"
                    >
                      <Thumb url={piece.primaryPhotoUrl} alt={piece.name} className="w-12 h-12 rounded-lg flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{piece.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {piece.brand ? `${piece.brand} · ` : ''}
                          {piece.category}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => setShowEdit(true)}>
                <Edit3 className="w-4 h-4" />
                Edit
              </Button>
              <Button variant="outline" onClick={() => setShowDelete(true)}>
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </>
        )}
      </Main>

      {/* Photo viewer */}
      {photoView && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setPhotoView(null)}>
          <button className="absolute top-4 right-4 p-2 text-white/80 hover:text-white" onClick={() => setPhotoView(null)}>
            <X className="w-6 h-6" />
          </button>
          <img src={photoView} alt="Wear" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Edit modal */}
      <Modal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        title="Edit Wear"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowEdit(false)} disabled={working}>
              Cancel
            </Button>
            <Button fullWidth onClick={handleSaveEdit} disabled={!wornDate || working}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Date *</label>
            <div className="relative">
              <input type="date" value={wornDate} onChange={(e) => setWornDate(e.target.value)} className={cn(inputClass, 'pl-10')} />
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Occasion</label>
            <button type="button" onClick={() => setShowOccasion(true)} className={cn(inputClass, 'flex items-center justify-between')}>
              <span className={occasion ? 'text-foreground' : 'text-muted-foreground'}>
                {occasion ? OCCASIONS.find((o) => o.id === occasion)?.label : 'Select occasion'}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Location</label>
            <div className="relative">
              <input
                type="text"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder="Where did you wear this?"
                className={cn(inputClass, 'pl-10')}
              />
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={cn(inputClass, 'resize-none')} />
          </div>
        </div>
      </Modal>

      <PickerSheet isOpen={showOccasion} onClose={() => setShowOccasion(false)} title="Select Occasion">
        <div className="space-y-1">
          {OCCASIONS.map((o) => (
            <PickerRow
              key={o.id}
              label={o.label}
              selected={occasion === o.id}
              onClick={() => {
                setOccasion(o.id);
                setShowOccasion(false);
              }}
            />
          ))}
        </div>
      </PickerSheet>

      {/* Delete confirm */}
      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Wear"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)} disabled={working}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={working}>
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Delete this wear log? This will also update wear counts for the items in this fit.
        </p>
      </Modal>
    </Page>
  );
}

function Detail({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}
